import { DataTable, type DataColumn } from '@/components/qxo/qxoBoardPrimitives';
import { resolveDataset, type DatasetDef } from '@/components/qxo/qxoDatasetCatalog';
import { seriesRead } from '@/components/qxo/qxoSeriesRead';
import type { StreetSnapshot } from '@/components/qxo/qxoStreet';

export function QxoDatasetFocus({
  id,
  snapshot,
  onClose,
}: {
  id: string;
  snapshot: StreetSnapshot | null;
  onClose: () => void;
}) {
  const dataset = resolveDataset(id, snapshot);
  if (!dataset) {
    return (
      <div className="mb-6 rounded-2xl bg-[#071f38] px-5 py-4 text-white">
        <button type="button" onClick={onClose} className="text-[12px] text-sky-300 hover:text-white">
          ← All QXO datasets
        </button>
        <p className="mt-3 text-[13px] text-sky-200">No dataset named “{id}”.</p>
      </div>
    );
  }
  return (
    <div className="mb-4 rounded-2xl bg-[#071f38] px-5 py-4 text-white">
      <button type="button" onClick={onClose} className="text-[12px] text-sky-300 hover:text-white">
        ← All QXO datasets
      </button>
      <p className="mt-3 text-[11px] uppercase tracking-wide text-sky-400">{dataset.board}</p>
      <h2 className="mt-1 text-lg font-semibold">{dataset.title}</h2>
      <p className="mt-3 text-[13px] leading-5 text-sky-100">
        <span className="font-medium text-white">What it is. </span>
        {dataset.represents}
      </p>
      <p className="mt-2 text-[13px] leading-5 text-sky-100">
        <span className="font-medium text-white">Why it matters for QXO. </span>
        {dataset.relatesToQxo}
      </p>
      <p className="mt-3 text-[11px] leading-4 text-sky-400">
        {dataset.coverage}. Source: {dataset.source}. {dataset.rows.length} rows. {coverageNote(dataset.coverage)}
      </p>
      <SeriesReadBlock id={dataset.id} />
    </div>
  );
}

function coverageNote(coverage: string): string {
  if (coverage.includes('2011') || coverage.includes('2012')) {
    return 'The opened chart uses this full history. The overview keeps a shorter window so the small charts stay readable.';
  }
  return 'This is the full published series. It is not extended before the first real print.';
}

function SeriesReadBlock({ id }: { id: string }) {
  const read = seriesRead(id);
  if (!read) return null;
  return (
    <div className="mt-4 grid gap-3 lg:grid-cols-3">
      <ReadCard label="Where it sits" body={read.where} />
      <ReadCard label="Where it is headed" body={read.heading} />
      <ReadCard label="What it means for QXO" body={read.forQxo} />
    </div>
  );
}

function ReadCard({ label, body }: { label: string; body: string }) {
  return (
    <div className="rounded-xl bg-[#0d3358] px-3 py-3">
      <p className="text-[11px] uppercase tracking-wide text-sky-400">{label}</p>
      <p className="mt-1 text-[13px] leading-5 text-sky-100">{body}</p>
    </div>
  );
}

export function QxoDatasetTable({ id, snapshot }: { id: string; snapshot: StreetSnapshot | null }) {
  const dataset = resolveDataset(id, snapshot);
  if (!dataset) return null;
  return (
    <div className="mt-4 rounded-2xl bg-[#071f38] p-4 text-white">
      <h3 className="text-[14px] font-semibold">Every observation</h3>
      <p className="mt-1 text-[12px] text-sky-300">
        {dataset.rows.length} rows in {dataset.title}. Empty cells are missing prints, not zeros.
      </p>
      <div className="mt-3">
        <DatasetRows dataset={dataset} />
      </div>
    </div>
  );
}

function DatasetRows({ dataset }: { dataset: DatasetDef }) {
  const columns: DataColumn<Record<string, string>>[] = dataset.columns.map((col) => ({
    key: col.key,
    header: col.header,
    align: col.align,
    render: (row) => row[col.key] ?? '—',
  }));
  return (
    <DataTable
      rows={dataset.rows}
      columns={columns}
      rowKey={(row) => Object.values(row).join('|')}
      dense
    />
  );
}
